<script setup>
/* (Der Knopf "Select" ist seit F2 keine Filterfunktion mehr: SelectToggle.vue.)
   Positionsfilter und Sortierknöpfe — für Kaderseite und Transfers-Seite (je Seite eigener Zustand,
   siehe useListView.js). Zwei Wurzelelemente (Filter, Sortierung); der umgebende Container
   (.squad-controls) steht in der Seite, ebenso die Jahrgangsknöpfe der Kaderseite.

   showDate: zusätzlicher Knopf "Transfer date" (nur Transfers-Seite). */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { mdiArrowUp, mdiArrowDown } from '@mdi/js';
import { FILTER_GROUPS } from '../domain/positions.js';

defineProps({
  counts: { type: Object, required: true },
  showDate: { type: Boolean, default: false },
});
const position = defineModel('position', { type: String, required: true });
const sort = defineModel('sort', { type: String, required: true });
const direction = defineModel('direction', { type: String, required: true });

const { t } = useI18n();

/* Reihenfolge der Filterknöpfe entspricht POSITION_GROUPS (Striker zuerst, Tor zuletzt), "Alle" davor. */
const FILTER_KEYS = ['all', ...FILTER_GROUPS];

/* Sortierung nach Alter: erster Klick = jüngster zuerst (Pfeil nach oben), jeder weitere Klick auf den
   aktiven Knopf kehrt um (Pfeil nach unten). Die Richtung wird beim Wechsel der Sortierung nicht
   zurückgesetzt. Der Pfeil ist ein Symbol im Knopf, nicht nur Farbe. */
const ageLabel = computed(() =>
  t(direction.value === 'youngest' ? 'squad.ageYoungestFirst' : 'squad.ageOldestFirst')
);
function onAge() {
  if (sort.value === 'age') direction.value = direction.value === 'youngest' ? 'oldest' : 'youngest';
  else sort.value = 'age';
}
</script>

<template>
  <div class="filter-group" role="group" :aria-label="$t('squad.filterLabel')">
    <button
      v-for="key in FILTER_KEYS"
      :key="key"
      type="button"
      class="btn ghost small"
      :class="{ active: position === key }"
      :aria-pressed="position === key"
      @click="position = key"
    >
      {{ key === 'all' ? $t('squad.filterAll') : $t(`positionGroup.${key}`) }}
      ({{ counts[key] || 0 }})
    </button>
  </div>

  <div class="sort-group" role="group" :aria-label="$t('squad.sortLabel')">
    <button
      type="button"
      class="btn ghost small"
      :class="{ active: sort === 'position' }"
      :aria-pressed="sort === 'position'"
      @click="sort = 'position'"
    >
      {{ $t('squad.sortByPosition') }}
    </button>
    <button
      type="button"
      class="btn ghost small"
      :class="{ active: sort === 'az' }"
      :aria-pressed="sort === 'az'"
      @click="sort = 'az'"
    >
      {{ $t('squad.sortAlphabetical') }}
    </button>
    <button
      type="button"
      class="btn ghost small age-sort"
      data-testid="sort-age"
      :class="{ active: sort === 'age' }"
      :aria-pressed="sort === 'age'"
      :aria-label="`${$t('squad.sortByAge')}, ${ageLabel}`"
      :title="ageLabel"
      @click="onAge"
    >
      {{ $t('squad.sortByAge') }}
      <v-icon :icon="direction === 'youngest' ? mdiArrowUp : mdiArrowDown" size="14" />
    </button>
    <button
      v-if="showDate"
      type="button"
      class="btn ghost small"
      data-testid="sort-date"
      :class="{ active: sort === 'date' }"
      :aria-pressed="sort === 'date'"
      @click="sort = 'date'"
    >
      {{ $t('squadPages.sortByTransferDate') }}
    </button>
  </div>

</template>

<style scoped>
.age-sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
